import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEditUserManagementMutation } from 'src/api/mutations/userManagementMutation';
import { useAlerts } from 'src/common/AlertManager';
import { Button, Checkbox, Input, Select } from 'src/components';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import useForm from 'src/hooks/useForm';
import { useAuthUser } from 'react-auth-kit';
import * as yup from 'yup';

import styles from '../styles.module.scss';
import parentStyles from '../../../styles.module.scss';
import messages from '../../../messages';

interface AccountTabProps {
  user: any;
  onEditUser?: () => void;
}

const roleOptions = [
  { label: 'ADMINISTRATOR', value: '1' },
  { label: 'MANAGER', value: '2' },
  { label: 'USER', value: '3' },
  { label: 'CREATOR', value: '4' },
];

const EditUserSchema = yup.object().shape({
  email: yup.string().email().label('Email'),
  first_name: yup.string().min(1).label('First name'),
  last_name: yup.string().min(1).label('Last name'),
  role_id: yup.number().label('Role'),
});

const AccountTab: React.FC<AccountTabProps> = ({ user, onEditUser }) => {
  const { form, handleChange, setForm } = useForm({
    email: user?.email,
    first_name: user?.first_name,
    last_name: user?.last_name,
  });

  const [errors, setErrors] = useState<string | undefined>();
  const auth = useAuthUser();
  const { showSuccess, showAlert } = useAlerts();
  const { getErrorMessage } = useErrorMessages();
  const { formatMessage } = useIntl();

  const editUserMutation = useEditUserManagementMutation(user?.id ?? -1, {
    onSuccess: () => {
      showSuccess(formatMessage(messages.userEditedSuccessfully, { email: user?.email }));
      onEditUser?.();
    },
    onError: (error: any) => {
      showAlert(getErrorMessage(error));
    },
  });

  const getUpdatedFields = () => {
    const fields = Object.keys(form);
    const data = { ...form };
    fields.forEach((field) => {
      if (data[field] === user?.[field]) delete data[field];
      if (field === 'new_password' && data[field] === '') delete data[field];
    });
    return data;
  };

  const hasEdited = useMemo(() => Object.keys(getUpdatedFields()).length > 0, [form]);

  const isSameUser = useMemo(() => {
    const authenticatedUser = auth();
    if (!authenticatedUser) return false;
    return authenticatedUser?.userId === user?.id;
  }, [user]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (!hasEdited) return;
    const data = getUpdatedFields();
    try {
      await EditUserSchema.validate(data);
      editUserMutation.mutateAsync({ ...data });
    } catch (error: any) {
      setErrors(error.message);
    }
  };

  useEffect(() => {
    setForm({
      email: user?.email,
      first_name: user?.first_name,
      last_name: user?.last_name,
      role_id: user?.role_id,
      new_password: '',
      enabled: user?.enabled,
      group_asset_access: user?.group_asset_access,
    });
  }, [user]);

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.formCard}>
        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={(value) => handleChange('email', value)}
          variant="secondary"
        />
        <div className={parentStyles.inlineGroup}>
          <Input
            label={formatMessage(messages.firstName)}
            value={form.first_name}
            onChange={(value) => handleChange('first_name', value)}
            variant="secondary"
          />
          <Input
            label={formatMessage(messages.lastName)}
            value={form.last_name}
            onChange={(value) => handleChange('last_name', value)}
            variant="secondary"
          />
        </div>

        <Input
          label={formatMessage(messages.newPassword)}
          type="password"
          variant="secondary"
          value={form.new_password}
          onChange={(value) => handleChange('new_password', value)}
        />

        {!isSameUser && (
          <div>
            <p className={styles.formCardField}>
              <label><FormattedMessage {...messages.role} /></label>
            </p>
            <Select
              options={roleOptions}
              value={form.role_id}
              onChange={(value) => handleChange('role_id', value)}
              marginTopstyle="-30px"
            />
          </div>
        )}

        {!isSameUser && (
          <div className={parentStyles.inlineGroup}>
            <Checkbox
              label={formatMessage(messages.enabled)}
              checked={form.enabled}
              onChangeCapture={() => handleChange('enabled', !form.enabled)}
            />
            <Checkbox
              label={
                <span>
                  {formatMessage(messages.allowUserGroupAssets)}
                  <br />
                  <span style={{fontSize:'var(--font-size-xs)',opacity:0.6,fontWeight:'normal'}}>
                    When enabled, this user can view and access assets shared across their user group.
                  </span>
                </span>
              }
              checked={form.group_asset_access}
              onChangeCapture={() => handleChange('group_asset_access', !form.group_asset_access)}
            />
          </div>
        )}
      </div>

      {errors && <p className={parentStyles.errorMessage}>{errors}</p>}

      <div className={styles.settingsActions}>
        <Button type="submit" mode="primary" size="small" disabled={!hasEdited}>
          <FormattedMessage {...messages.submit} />
        </Button>
      </div>
    </form>
  );
};

export default AccountTab;
